import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  CopyButton,
  FullscreenButton,
  HistoryPanel,
  Panel,
  ResetButton,
  SoundToggle,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import { randomInt } from "@/lib/random-engine";
import { CircleDollarSign, RotateCcw } from "lucide-react";

export function CoinFlip() {
  const [count, setCount] = useState(1);
  const [flipping, setFlipping] = useState(false);
  const [results, setResults] = useState<("Heads" | "Tails")[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [stats, setStats] = useState({ heads: 0, tails: 0 });
  const [soundOn, setSoundOn] = usePersistentState("pickamini:coin:sound", true);

  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);

  const flip = () => {
    if (flipping) return;
    setFlipping(true);
    sfx.whoosh();

    const outcomes = Array.from({ length: count }, () =>
      randomInt(2) === 0 ? ("Heads" as const) : ("Tails" as const),
    );

    // Rapid flip animation (~500ms)
    window.setTimeout(() => {
      setResults(outcomes);
      setFlipping(false);
      sfx.fanfare();

      // Update history & stats
      const headsCount = outcomes.filter((r) => r === "Heads").length;
      const tailsCount = outcomes.length - headsCount;

      setStats((prev) => ({
        heads: prev.heads + headsCount,
        tails: prev.tails + tailsCount,
      }));

      const summary =
        count === 1
          ? outcomes[0]!
          : `${count} Coins: ${headsCount} Heads, ${tailsCount} Tails (${outcomes.join(", ")})`;

      setHistory((h) => [summary, ...h].slice(0, 50));
    }, 550);
  };

  const totalFlips = stats.heads + stats.tails;

  return (
    <div className="grid gap-5 lg:grid-cols-[320px_1fr_300px]">
      {/* Settings Panel */}
      <Panel title="Coin Setup" className="no-print space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium">Number of Coins</label>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 5, 10].map((n) => (
              <Button
                key={n}
                size="sm"
                variant={count === n ? "default" : "outline"}
                className="font-semibold"
                onClick={() => setCount(n)}
              >
                {n} {n === 1 ? "Coin" : "Coins"}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <ResetButton
            onClick={() => {
              setCount(1);
              setResults([]);
              setHistory([]);
              setStats({ heads: 0, tails: 0 });
            }}
          />
        </div>
      </Panel>

      {/* Main Coin Stage */}
      <div
        ref={stageRef}
        className="surface-card relative flex flex-col items-center justify-between gap-6 p-8 min-h-[360px]"
      >
        <div className="w-full flex items-center justify-between no-print">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <CircleDollarSign className="size-4 text-primary" />
            <span>
              {count} {count === 1 ? "Coin" : "Coins"}
            </span>
          </div>
          <FullscreenButton targetRef={stageRef} />
        </div>

        {/* Coins Display */}
        <div className="flex flex-wrap items-center justify-center gap-6 my-auto py-4">
          {(results.length ? results : Array.from({ length: count }, () => null)).map(
            (res, idx) => (
              <div
                key={idx}
                className={`relative flex size-32 sm:size-36 cursor-pointer select-none flex-col items-center justify-center rounded-full border-4 shadow-lift transition-all ${
                  flipping ? "animate-coin" : "animate-reveal"
                } ${
                  res === "Heads"
                    ? "border-amber-400 bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 text-slate-950"
                    : res === "Tails"
                      ? "border-slate-300 bg-gradient-to-br from-slate-200 via-slate-300 to-slate-500 text-slate-900"
                      : "border-primary/40 bg-accent-gradient text-accent-foreground"
                }`}
              >
                <span className="text-2xl sm:text-3xl font-black tracking-wider uppercase font-display">
                  {flipping ? "?" : (res ?? "FLIP")}
                </span>
                <span className="text-[11px] font-bold uppercase opacity-75 mt-0.5">
                  {flipping ? "Flipping" : res ? res : "Click flip"}
                </span>
              </div>
            ),
          )}
        </div>

        {/* Action Button & Result */}
        <div className="flex flex-col items-center gap-3 w-full">
          <p className="font-display text-2xl font-bold text-foreground min-h-8">
            {flipping
              ? "Flipping…"
              : results.length === 1
                ? results[0]
                : results.length > 1
                  ? `${results.filter((r) => r === "Heads").length} Heads, ${results.filter((r) => r === "Tails").length} Tails`
                  : "Tap Flip Coin"}
          </p>

          <Button
            size="lg"
            className="h-14 px-10 text-lg font-bold"
            disabled={flipping}
            onClick={flip}
          >
            <RotateCcw className={`size-5 mr-2 ${flipping ? "animate-spin" : ""}`} />
            {flipping ? "Flipping…" : count > 1 ? `Flip ${count} Coins` : "Flip Coin"}
          </Button>
        </div>
      </div>

      {/* Stats & History Panel */}
      <div className="space-y-4">
        <Panel title="Coin Stats" className="no-print">
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="rounded-xl bg-secondary/70 p-3">
              <span className="text-xs text-muted-foreground block font-medium">Heads</span>
              <span className="font-display text-2xl font-bold text-amber-500">
                {stats.heads}
              </span>
              <span className="text-[11px] text-muted-foreground block">
                {totalFlips ? `${Math.round((stats.heads / totalFlips) * 100)}%` : "0%"}
              </span>
            </div>
            <div className="rounded-xl bg-secondary/70 p-3">
              <span className="text-xs text-muted-foreground block font-medium">Tails</span>
              <span className="font-display text-2xl font-bold text-slate-600 dark:text-slate-300">
                {stats.tails}
              </span>
              <span className="text-[11px] text-muted-foreground block">
                {totalFlips ? `${Math.round((stats.tails / totalFlips) * 100)}%` : "0%"}
              </span>
            </div>
          </div>
        </Panel>

        <HistoryPanel items={history} onClear={() => setHistory([])} title="Flip History" />
      </div>
    </div>
  );
}
